import { ReactNode, useEffect, useRef, useState } from 'react'

import { Portal } from 'ui/components/Portal'
import { useDelayMount } from 'ui/hooks/useDelayMount'

import { Box } from './Box'
import { SidePanelContext } from './context'

import * as SC from './styles'

type Props = {
  isOpened?: boolean
  showBlackout?: boolean
  content: ReactNode

  referenceNode?: Element
  type?: 'forErrors' | 'forTables'

  /* TODO: find proper type */
  renderReference?: (props: any) => ReactNode
  onOpenClose?: (isOpened: boolean) => void
}

/*

HOW TO USE

variant 1, preferred

<SidePanel
  content={(
    <>
      <SidePanelHeader> Header </SidePanelHeader>
      <SidePanelContent> Content </SidePanelContent>
      <SidePanelFooter>
        <Button onClick={() => context.close()}>Cancel</Button>
        <Button loading={context.isBlocked} onClick={() => context.block(true)}>
          Action
        </Button>
      </SidePanelFooter>
    </>
  )}
  renderReference={(props) => (
    <Link {...props}>Click me to open side panel</Link>
  )}
/>

variant 2

const [open, setOpen] = useState(false)

<SidePanel
  isOpened={open}
  content={(
    <>
      <SidePanelHeader> Header </SidePanelHeader>
      <SidePanelContent> Content </SidePanelContent>
      <SidePanelFooter>
        <Button onClick={() => context.close()}>Cancel</Button>
        <Button loading={context.isBlocked} onClick={() => context.block(true)}>
          Action
        </Button>
      </SidePanelFooter>
    </>
  )}
  referenceNode={} // an element in the table row for attaching `sidepanel` class (to make table row underlined)
  onOpenClose={(open) => setOpen(open)}
/>

<Button
  onClick={() => {
    if (!open) {
      setOpen(true)
    } else {
      setTimeout(() => setOpen(true), 250)
    }
  }}
>
  Click me to open side panel
</Button>

*/

export const SidePanel = (props: Props) => {
  const {
    type = 'forTables',
    showBlackout = false,
    isOpened: _isOpened = false,
    content,
    renderReference,
    referenceNode,
    onOpenClose
  } = props

  const ref = useRef(null)

  const [isBlocked, setIsBlocked] = useState(false)
  const [isOpened, setIsOpened] = useState(_isOpened)

  const isShowSidePanel = useDelayMount(isOpened, 250, (showDiv) => {
    if (onOpenClose) {
      onOpenClose(showDiv)
    }
  })

  useEffect(() => {
    setIsOpened(_isOpened)
  }, [_isOpened])

  const openAction = () => {
    setIsOpened(true)
  }

  const closeAction = () => {
    if (isBlocked) {
      return
    }
    setIsOpened(false)
  }

  return (
    <>
      {renderReference &&
        renderReference({
          onClick: (e) => {
            e.preventDefault()
            e.stopPropagation()
            openAction()
          },
          ref: (node) => {
            ref.current = node
          }
        })}
      {isShowSidePanel && (
        <SidePanelContext.Provider
          value={{
            isOpened,
            isBlocked,
            block: (isBlocked) => setIsBlocked(isBlocked),
            close: closeAction
          }}
        >
          <Portal id={'side-panel'}>
            {(showBlackout || isBlocked) && <SC.Overlay $opened={isOpened} />}
            <Box
              isCloseOnOutsideClick={type !== 'forErrors'}
              isBlockScrolling={showBlackout || isBlocked}
              referenceRef={ref}
              referenceNode={referenceNode}
            >
              {content}
            </Box>
          </Portal>
        </SidePanelContext.Provider>
      )}
    </>
  )
}
